/** @file Checks a puppet's drawables: unique ids, known nodes, anchors, lerps, facet and effect references. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { Drawable, ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { validateFacetTable } from '@zilo/pose3d-format/validate/validateFacetTable';

function checkNode(node: string, path: string, puppet: Puppet, hint?: string): void {
  if (!Object.hasOwn(puppet.nodes, node)) throwError(ERROR_CODES.DRAWABLE_NODE_UNKNOWN, { node }, hint === undefined ? { path } : { path, hint });
}

/**
 * The second points and blends of an impostor (21.5, 21.7): `anchor.node` and `lerp.node` name existing nodes;
 * `anchor.offset` (a point along the drawable node's own bone) and `anchor.node` (another node's position) are two
 * answers to one question, so a document gives one; `lerp.node` is another node than the drawable's own.
 */
function checkSecondPoints(d: ImpostorDrawable, path: string, puppet: Puppet): void {
  if (d.anchor?.node !== undefined) checkNode(d.anchor.node, `${path}/anchor/node`, puppet);
  if (d.anchor?.offset !== undefined && d.anchor.node !== undefined) {
    failField(`${path}/anchor/offset`, 'no offset while anchor.node is set', d.anchor.offset,
      "anchor.offset places the second point along this node's own bone (21.7); anchor.node uses that node's position: keep one");
  }
  if (d.lerp === undefined) return;
  checkNode(d.lerp.node, `${path}/lerp/node`, puppet, 'lerp.node names the node whose drawable point this one blends toward (21.7)');
  if (d.lerp.node === d.node) {
    failField(`${path}/lerp/node`, "a node other than the drawable's own", d.lerp.node,
      "a lerp toward the drawable's own node changes nothing: name the other node, or drop lerp");
  }
}

/** Per-kind reference checks (exhaustive over `Drawable['kind']`). */
function checkKind(d: Drawable, path: string, puppet: Puppet): void {
  switch (d.kind) {
    case 'impostor':
      checkSecondPoints(d, path, puppet);
      validateFacetTable(d.facets, `${path}/facets`, puppet);
      return;
    case 'effect':
      if (!Object.hasOwn(puppet.sheets, d.frames.sheet)) {
        throwError(ERROR_CODES.SHEET_UNKNOWN, { sheet: d.frames.sheet }, { path: `${path}/frames/sheet` });
      }
      if (d.facets !== undefined) validateFacetTable(d.facets, `${path}/facets`, puppet);
      return;
    case 'mesh':
    case 'decal':
      return;
  }
}

/** Throws the first reference error among `puppet.drawables` (paths under `/drawables`). */
export function validateDrawables(puppet: Puppet): void {
  const seen = new Set<string>();
  puppet.drawables.forEach((d, i) => {
    const path = `/drawables/${i}`;
    if (seen.has(d.id)) throwError(ERROR_CODES.DRAWABLE_DUPLICATE_ID, { id: d.id }, { path: `${path}/id` });
    seen.add(d.id);
    checkNode(d.node, `${path}/node`, puppet);
    checkKind(d, path, puppet);
  });
}
